import React, { FC } from 'react'

import { Box, Theme, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CopyText from '../../components/CopyText/CopyText'

import { IErrorProps } from './types'
import { generateStack } from './utils'

const errorBodyStyle = {
  '& p': {
    fontSize: 14,
    lineHeight: 1.4,
    color: (theme: Theme) => theme.palette.text.secondary,
    fontWeight: 500,
  },
}

const ErrorInfo: FC<IErrorProps> = ({ data }) => {
  const { spacing, palette } = useTheme()
  const { t } = useTranslation()
  const frontendError = data?.frontendError
  const backendError = data?.backendError
  const frontendCode = frontendError?.code
  const frontendStack = frontendError?.stack
  const frontendMessage = frontendError?.message
  const frontendTime = frontendError?.time
  const frontendClipboard = generateStack(
    frontendCode,
    frontendStack,
    frontendMessage,
    frontendTime,
  )

  const backendCode = backendError?.backendResponseData.statusCode
  const backendStack = backendError?.stack
  const backendMessage = backendError?.backendResponseData.message
  const backendTime = backendError?.backendResponseData.time
  const backendClipboard = generateStack(backendCode, backendStack, backendMessage, backendTime)

  let clipboardData = ''

  if (frontendError) clipboardData = frontendClipboard
  if (backendError) clipboardData = backendClipboard
  if (frontendError && backendError) {
    clipboardData = `
      ${frontendClipboard}
      ------------------
      ${backendClipboard}
    `
  }

  return (
    <Box marginTop={spacing(4)}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        p={spacing(1.5, 2)}
        borderRadius={spacing(0.5, 0.5, 0, 0)}
        border={`1px solid ${palette.grey[500]}`}
      >
        <Typography variant="h6">{t('errorPage.detailsPanel.title', 'Error details')}</Typography>
        <CopyText
          copyText={clipboardData}
          title={{ key: 'errorPage.detailsPanel.copy', defaultValue: 'Copy message' }}
        />
      </Box>

      <Box
        p={spacing(1.5, 2)}
        bgcolor={palette.background.default}
        border={`1px solid ${palette.grey[500]}`}
        borderRadius={spacing(0, 0, 0.5, 0.5)}
        textAlign={'left'}
        fontSize={14}
        color={'text.secondary'}
        fontWeight={500}
        sx={errorBodyStyle}
      >
        {frontendError && (
          <>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.fields.code', 'Error Code')}: {frontendCode}
            </Typography>

            <Typography variant="body1">
              {t('errorPage.detailsPanel.fields.message', 'Error Message')}: {frontendMessage}
            </Typography>
          </>
        )}

        {backendError && (
          <>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.fields.code', 'Error Code')}: {backendCode}
            </Typography>

            <Typography variant="body1">
              {t('errorPage.detailsPanel.fields.message', 'Error Message')}: {backendMessage}
            </Typography>
          </>
        )}

        <br />

        {frontendError && (
          <>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.title', 'Technical Details')}:
            </Typography>
            <Typography variant="body1">------------------</Typography>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.timestamp', 'Error Timestamp')}:&nbsp;
              <span>{frontendTime}</span>
            </Typography>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.stackTrace', 'Error Stack Trace')}:&nbsp;
              <span>{frontendStack?.toString()}</span>
            </Typography>
          </>
        )}

        {backendError && (
          <>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.title', 'Technical Details')}:
            </Typography>
            <Typography variant="body1">------------------</Typography>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.timestamp', 'Error Timestamp')}:&nbsp;
              <span>{backendTime}</span>
            </Typography>
            <Typography variant="body1">
              {t('errorPage.detailsPanel.technical.stackTrace', 'Error Stack Trace')}:&nbsp;
              <span>{backendStack?.toString()}</span>
            </Typography>
          </>
        )}

        <br />
        <Typography variant="body1">
          {t(
            'errorPage.detailsPanel.support',
            'Please contact our support team for assistance if the issue persists. We appreciate your understanding.',
          )}
        </Typography>
      </Box>
    </Box>
  )
}

export default ErrorInfo
